import React, { useState, useEffect } from 'react';
import { videoAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';

const VIDEO_CATEGORIES = ['Interviews', 'Bangalore Metro TV', 'Karnataka Police TV'];

const VideoList = () => {
    const [loading, setLoading] = useState(false);
    const [videos, setVideos] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Search & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await videoAPI.getAll();
            setVideos(res.data.data);
        } catch (error) {
            console.error('Error fetching videos:', error);
            toast.error('Failed to load videos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Filter & Paginate
    const filteredItems = videos.filter(item =>
        item.video_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.video_category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.video_url?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Edit handlers
    const handleEditClick = (item) => {
        setSelectedItem({ ...item });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setSelectedItem(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            await videoAPI.update(selectedItem.id, {
                video_title: selectedItem.video_title,
                video_url: selectedItem.video_url,
                video_category: selectedItem.video_category,
            });
            toast.success('Video updated successfully!');
            setShowEditModal(false);
            fetchItems();
        } catch (error) {
            console.error(error);
            const errors = error.response?.data?.errors;
            const msg = errors ? Object.values(errors).flat().join(' ') : 'Error updating video.';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // Delete handlers
    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p className="mb-2">Are you sure you want to delete this video?</p>
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-sm btn-danger mr-2"
                        onClick={() => {
                            toast.dismiss();
                            handleDeleteItem(id);
                        }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeOnClick: false,
                closeButton: false,
                icon: false,
            }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await videoAPI.delete(id);
            toast.success('Video deleted successfully!');
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error deleting video.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="main-content">
                <div className="content-wrapper">
                    <div className="list-card">
                        <div className="list-card-header d-flex justify-content-between align-items-center">
                            <h5><i className="fas fa-video me-2"></i>All Videos</h5>
                            <div style={{ width: '250px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by title or URL..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="list-card-body">
                            {loading && <p>Loading...</p>}
                            {!loading && filteredItems.length === 0 && <p>No videos found.</p>}
                            {!loading && filteredItems.length > 0 && (
                                <>
                                    <div className="table-responsive">
                                        <table className="display compact table table-striped table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Title</th>
                                                    <th>Category</th>
                                                    <th>URL</th>
                                                    <th>Preview</th>
                                                    <th>Edit</th>
                                                    <th>Delete</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedItems.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                        <td>{item.video_title}</td>
                                                        <td>{item.video_category}</td>
                                                        <td>
                                                            <a href={item.video_url} target="_blank" rel="noopener noreferrer">
                                                                {item.video_url.length > 40 ? item.video_url.substring(0, 40) + '...' : item.video_url}
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a href={item.video_url} target="_blank" rel="noopener noreferrer">
                                                                <i className="fas fa-play-circle" style={{ fontSize: '20px', color: 'red' }}></i>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a href="#" onClick={(e) => { e.preventDefault(); handleEditClick(item); }}>
                                                                <i className="fas fa-edit"></i>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a href="#" onClick={(e) => { e.preventDefault(); confirmDelete(item.id); }}>
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="mt-3">
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </button>
                                                </li>
                                                {[...Array(totalPages).keys()].map(num => (
                                                    <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(num + 1)}>
                                                            {num + 1}
                                                        </button>
                                                    </li>
                                                ))}
                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <ToastContainer />
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Video</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <form>
                            <div className="mb-3">
                                <label>Video Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="video_title"
                                    value={selectedItem.video_title || ''}
                                    onChange={handleEditChange}
                                />
                            </div>
                            <div className="mb-3">
                                <label>Category</label>
                                <select
                                    className="form-control"
                                    name="video_category"
                                    value={selectedItem.video_category || ''}
                                    onChange={handleEditChange}
                                >
                                    {VIDEO_CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <label>Video URL</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    name="video_url"
                                    value={selectedItem.video_url || ''}
                                    onChange={handleEditChange}
                                />
                            </div>
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={loading}>
                        {loading ? 'Updating...' : 'Update'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default VideoList;